import axios /*, { AxiosError }*/ from "axios";
import { destroyCookie, parseCookies, setCookie } from "nookies";

// Nome do evento global disparado quando o back rejeita o token (expirado
// por inatividade ou inválido). O AuthContext escuta esse evento pra
// deslogar o usuário de fato e voltar pra tela de login, em vez de deixar
// a tela atual estourar um erro solto.
export const SESSAO_EXPIRADA_EVENT = "auth:sessao-expirada";

export const TOKEN_COOKIE = "@portal.customax.token";

// Rotas da SPA (ver routes/app.routes.js e routes/auth.routes.js). Precisam estar aqui porque um
// cookie gravado sem `path` explícito fica preso no path da página em que foi gravado.
const ROTAS_CONHECIDAS = [
  "/app",
  "/app/chamado",
  "/app/chamados",
  "/app/change-pass",
  "/app/newpass",
  "/app/changepass",
];

/**
 * Todo path em que uma cópia do cookie do token pode estar escondida.
 *
 * O navegador guarda cookies de mesmo nome em paths diferentes como entradas SEPARADAS, e manda
 * todas na requisição — a de path mais específico primeiro. Como `parseCookies` devolve um valor
 * só por nome, é a cópia mais específica que vence. Uma versão antiga do Portal gravou o cookie
 * sem `path`, então ele ficou preso em `/app/...`: na Home (`/`) só existe a cópia boa e tudo
 * funciona, mas ao entrar em qualquer rota `/app/...` a cópia velha (e vencida) passa a ser
 * enviada e o usuário toma 401. Era exatamente o sintoma "clico no menu e ele me desloga".
 */
function pathsCandidatos() {
  const paths = new Set(["/"]);
  ROTAS_CONHECIDAS.forEach((p) => paths.add(p));

  const atual =
    typeof window !== "undefined" ? window.location.pathname || "/" : "/";
  let acumulado = "";
  atual
    .split("/")
    .filter(Boolean)
    .forEach((parte) => {
      acumulado += `/${parte}`;
      paths.add(acumulado);
    });

  return [...paths];
}

/** Apaga o cookie do token em todos os paths possíveis, não só em "/". */
export function limparTokenTodosOsPaths(ctx = undefined) {
  pathsCandidatos().forEach((path) => {
    destroyCookie(ctx, TOKEN_COOKIE, { path });
  });
  // Sem path também: cobre a cópia gravada no path default da página atual.
  destroyCookie(ctx, TOKEN_COOKIE);
}

/**
 * Há mais de uma cópia do cookie do token no navegador?
 *
 * `document.cookie` lista uma entrada por cópia, mesmo quando os nomes se repetem — é a única
 * forma de detectar o problema pelo JS, já que `parseCookies` colapsa tudo num valor só.
 */
export function tokenDuplicado() {
  if (typeof document === "undefined") return false;
  const copias = document.cookie
    .split(";")
    .filter((c) => c.trim().startsWith(`${TOKEN_COOKIE}=`));
  return copias.length > 1;
}

/**
 * Instante de expiração (ms) declarado no próprio JWT, ou null se não der pra ler.
 *
 * Só lê o campo `exp` do payload pra decidir se vale a pena guardar o token — não é validação de
 * segurança nenhuma (isso é do back, que confere a assinatura); é defesa contra guardar um token
 * que já nasce inútil.
 */
function expiracaoDoToken(token) {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

export function setupAPIClient(ctx = undefined) {
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || "/api",
  });

  // O token vem do cookie a CADA requisição, não uma única vez na criação do client. Antes o
  // `Authorization` era congelado no load do módulo — e como `api` é um singleton importado na
  // primeira renderização (tela de login, quando ainda não há cookie), qualquer request que
  // escapasse da atualização manual de `api.defaults` saía com `Bearer undefined` → 401 →
  // "sessão expirou". Lendo do cookie aqui, login/refresh/logout têm uma fonte única de verdade.
  api.interceptors.request.use((config) => {
    const token = parseCookies(ctx)[TOKEN_COOKIE];
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    } else {
      delete config.headers["Authorization"];
    }

    // Força revalidação em vez de aceitar resposta guardada pelo navegador. O back e o nginx já
    // mandam `no-store`, mas isso só vale para respostas novas: quem já está com o cache
    // envenenado (resposta antiga guardada junto com um `x-refreshed-token` vencido) continuaria
    // sendo derrubado até a entrada velha morrer sozinha. Pedindo `no-cache` no request, a entrada
    // ruim é substituída no primeiro acesso — sem o usuário precisar limpar nada no navegador.
    config.headers["Cache-Control"] = "no-cache";
    config.headers["Pragma"] = "no-cache";

    return config;
  });

  api.interceptors.response.use(
    (response) => {
      // Sessão deslizante: o back renova o token a cada requisição
      // autenticada (24h de validade). Propaga o token renovado pro
      // cookie e pros próximos requests desta mesma sessão de página.
      const tokenRenovado = response.headers?.["x-refreshed-token"];
      // Nunca gravar um token renovado que já esteja vencido. Se a resposta vier de cache (foi o
      // que causou o incidente de 2026-08-17), esse header é de uma sessão antiga e sobrescrever o
      // cookie com ele derruba um usuário que estava perfeitamente logado. Descartar é seguro: o
      // token atual do cookie continua valendo, e a próxima resposta de rede renova de novo.
      const expiraEm = tokenRenovado ? expiracaoDoToken(tokenRenovado) : null;
      if (tokenRenovado && (expiraEm === null || expiraEm > Date.now())) {
        setCookie(undefined, TOKEN_COOKIE, tokenRenovado, {
          maxAge: 60 * 60 * 24, // 24h
          path: "/",
        });
        api.defaults.headers["Authorization"] = `Bearer ${tokenRenovado}`;
      }
      return response;
    },
    (error) => {
      const isLoginRequest = error.config?.url?.includes("/usuario/login");
      const isLogoutRequest = error.config?.url?.includes("/usuario/logout");

      if (error.response?.status === 401 && !isLoginRequest && !isLogoutRequest) {
        // Limpa TODOS os paths, não só "/": o token recusado pode estar numa cópia presa em
        // `/app/...` (ver pathsCandidatos), e apagar só a cópia da raiz deixava a ruim viva,
        // realimentando o 401 na próxima rota do menu.
        limparTokenTodosOsPaths();
        delete api.defaults.headers["Authorization"];
        // Repassa o `codigo` do back (ver middlewares/estaAutenticado.ts) pra o AuthContext
        // escolher a mensagem certa em vez de culpar sempre a inatividade.
        window.dispatchEvent(
          new CustomEvent(SESSAO_EXPIRADA_EVENT, {
            detail: { codigo: error.response?.data?.codigo },
          })
        );
      }
      return Promise.reject(error);
    }
  );

  return api;
}
